import { $api } from "@/shared/api/api";
import AuthService from "@/shared/services/AuthService";
import { VKAuthCodeRequest, VKAuthResponse } from "@/shared/types/auth";

export type SocialProvider = "google" | "facebook" | "destiny_id" | "vk-oauth2";

export interface SocialLoginRequest {
  return_url?: string;
  ref?: string;
}

export interface SocialLoginResponse {
  iframe_url: string;
}

export default class SocialAuthService {
  /**
   * Начать процесс социальной авторизации
   * @param provider Провайдер социальной авторизации
   * @param data Данные для авторизации
   */
  static async startSocialAuth(
    provider: SocialProvider,
    data: SocialLoginRequest = {}
  ): Promise<SocialLoginResponse> {
    const response = await $api.post<SocialLoginResponse>(
      `/auth/social/${provider}/login`,
      data
    );
    return response.data;
  }

  /**
   * Получить URL для социальной авторизации
   * @param provider Провайдер социальной авторизации
   * @param returnUrl URL для возврата после авторизации
   */
  static async getSocialAuthUrl(
    provider: SocialProvider,
    returnUrl?: string
  ): Promise<string> {
    const data: SocialLoginRequest = {};
    if (returnUrl) {
      data.return_url = returnUrl;
    }

    const response = await this.startSocialAuth(provider, data);
    return response.iframe_url;
  }

  /**
   * Открыть окно социальной авторизации
   * @param provider Провайдер социальной авторизации
   * @param returnUrl URL для возврата после авторизации
   */
  static async openSocialAuthWindow(
    provider: SocialProvider,
    returnUrl?: string
  ): Promise<Window | null> {
    try {
      const authUrl = await this.getSocialAuthUrl(provider, returnUrl);

      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const popup = window.open(
        authUrl,
        `social-auth-${provider}`,
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );

      return popup;
    } catch (error) {
      console.error(`Error opening ${provider} auth window:`, error);
      throw error;
    }
  }

  /**
   * Обработать завершение социальной авторизации
   * Ожидает сообщение от popup окна с результатом авторизации
   */
  static waitForAuthCompletion(
    popup?: Window | null,
    timeoutMs: number = 300000
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    return new Promise((resolve) => {
      const allowedOrigins = new Set<string>();
      allowedOrigins.add(window.location.origin);
      try {
        if (import.meta.env.VITE_API_URL) {
          const apiOrigin = new URL(import.meta.env.VITE_API_URL as string)
            .origin;
          allowedOrigins.add(apiOrigin);
        }
      } catch {
        // игнорируем неверный VITE_API_URL
      }

      const cleanup = () => {
        window.removeEventListener("message", handleMessage as EventListener);
        if (closeCheckInterval) clearInterval(closeCheckInterval);
        if (timeoutId) clearTimeout(timeoutId);
      };

      const handleMessage = (event: MessageEvent) => {
        if (!allowedOrigins.has(event.origin)) {
          return;
        }

        // При возможности сверяем источник сообщения с popup
        if (popup && event.source && event.source !== popup) {
          return;
        }

        const rawData = event.data as unknown;
        const data = ((): { type?: unknown; error?: unknown } => {
          if (typeof rawData === "object" && rawData !== null) {
            return rawData as { type?: unknown; error?: unknown };
          }
          return {};
        })();

        if (data.type === "SOCIAL_AUTH_SUCCESS") {
          cleanup();
          resolve({ success: true });
        } else if (data.type === "SOCIAL_AUTH_ERROR") {
          const errorMessage =
            typeof data.error === "string" ? data.error : undefined;
          cleanup();
          resolve({ success: false, error: errorMessage });
        }
      };

      window.addEventListener("message", handleMessage as EventListener);

      // Периодически проверяем закрытие popup пользователем
      const closeCheckInterval = setInterval(() => {
        if (popup && popup.closed) {
          cleanup();
          resolve({ success: false, error: "Авторизация была отменена" });
        }
      }, 500);

      // Общий таймаут ожидания
      const timeoutId = setTimeout(() => {
        cleanup();
        resolve({ success: false, error: "Авторизация была отменена" });
      }, timeoutMs);
    });
  }

  /**
   * Обработка VK callback после редиректа
   * Использует /web/vk_auth/ согласно новой документации
   * @param url Полный URL редиректа с параметрами code, expires_in, device_id, state, type
   * @param codeVerifier PKCE code_verifier, полученный с фронтенда
   */
  static async vkAuth(
    url: string,
    codeVerifier: string
  ): Promise<VKAuthResponse> {
    const requestData: VKAuthCodeRequest = {
      url,
      code_verifier: codeVerifier,
    };

    const response = await $api.post<VKAuthResponse>(
      `/web/vk_auth/`,
      requestData
    );
    const tokens = response.data;

    AuthService.setTokens(tokens);

    return tokens;
  }

  /**
   * Привязка VK аккаунта к текущему пользователю
   * Использует /web/vk_account_connect/ согласно новой документации
   * @param url Полный URL редиректа с параметрами
   * @param codeVerifier PKCE code_verifier
   */
  static async connectVKAccount(
    url: string,
    codeVerifier: string
  ): Promise<void> {
    const requestData: VKAuthCodeRequest = {
      url,
      code_verifier: codeVerifier,
    };

    await $api.post(`/web/vk_account_connect/`, requestData);
  }

  /**
   * Отвязка VK аккаунта от текущего пользователя
   * Использует /web/vk_account_disconnect/ согласно новой документации
   * @param url Полный URL редиректа с параметрами
   * @param codeVerifier PKCE code_verifier
   */
  static async disconnectVKAccount(
    url: string,
    codeVerifier: string
  ): Promise<void> {
    const requestData: VKAuthCodeRequest = {
      url,
      code_verifier: codeVerifier,
    };

    await $api.post(`/web/vk_account_disconnect/`, requestData);
  }
}
